import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { InvitationMarkFilesAsRead } from "../../../../reduxToolkit/Invitation/InvitationApi";
import { MarkFilesAsRead } from "../../../../reduxToolkit/Reports/ReportApi";
import { RequestAccessMarkFilesAsRead } from "../../../../reduxToolkit/RequestAccess/RequestAccessApi";
import {
    ALLOWED_NOTIFICATION_ROUTES,
    NOTIFICATION_ITEM_MODEL, NOTIFICATION_TABS_MODEL
} from "../Model/NotificationsModel";


/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */

/**
 * Main logic for Notifications Button.
 * Contains state management, handlers, and report-related logic.
 */
export const NotificationsButtonApp = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation()
    const { t } = useTranslation();
    // -------- States --------
    const [open, setOpen] = useState(false);
    const [showButton, setShowButton] = useState(false)
    const [selected, setSelected] = useState(NOTIFICATION_TABS_MODEL.ALL.name);
    const [notifications, setNotifications] = useState({
        [NOTIFICATION_TABS_MODEL.ALL.name]: { list: [], unviewed: 0 },
        [NOTIFICATION_TABS_MODEL.REPORTS.name]: { list: [], unviewed: 0 },
        [NOTIFICATION_TABS_MODEL.VISITORS.name]: { list: [], unviewed: 0 }
    });
    const data = useSelector((state) => state.ReportSlice.notificationList);


    // -------- Handlers --------
    const handleToggle = () => setOpen((prev) => !prev);
    const handleClose = () => setOpen(false);


    useEffect(() => {

        const isPermittedShow = ALLOWED_NOTIFICATION_ROUTES.some((route) =>
            location.pathname.startsWith(`/${route}`)
        );

        setShowButton(isPermittedShow);
    }, [location]);


    /**
     * Marks all data as read.
     */
    const handleMarkAllReportsAsRead = async () => {
        const reportIds = data.reports.filter((e) => !e.isFileViewed).map((r) => r.id);
        const requestIds = data.requestAccess.filter((e) => !e.isViewed).map((r) => r.id);
        const invitationIds = data.userInvitation.filter((e) => e.isViewed === 1).map((r) => r.id);

        if (reportIds.length === 0 && requestIds.length === 0 && invitationIds.length === 0) {
            toast.warning(t("all_notifications_have_already_been_read"));
            return;
        }
        if (reportIds.length > 0)
            await dispatch(MarkFilesAsRead(reportIds));

        if (requestIds.length > 0)
            await dispatch(RequestAccessMarkFilesAsRead(requestIds));

        if (invitationIds.length > 0)
            await dispatch(InvitationMarkFilesAsRead(invitationIds));

        toast.success(t("has_been_marked_as_read"));
    };

    /**
     * Syncs Redux data and other sources into notifications state.
     */
    useEffect(() => {
        if (!data || Object.entries(data).length === 0) return;
        let reports = data?.reports?.map(e => NOTIFICATION_ITEM_MODEL.set(e, NOTIFICATION_ITEM_MODEL.REPORTS.key));
        reports = NOTIFICATION_ITEM_MODEL.sort(reports) || [];

        const requestAccess = data?.requestAccess?.map(e => NOTIFICATION_ITEM_MODEL.set(e, NOTIFICATION_ITEM_MODEL.REQUEST_ACCESS.key)) || [];
        const userInvitation = data?.userInvitation?.map(e => NOTIFICATION_ITEM_MODEL.set(e, NOTIFICATION_ITEM_MODEL.USER_INVITATION.key)) || [];
        const visitors = NOTIFICATION_ITEM_MODEL.sort([...requestAccess, ...userInvitation]);

        const all = NOTIFICATION_ITEM_MODEL.sort([...reports, ...requestAccess, ...userInvitation]);
        const unviewed = all.filter(e => !e.read).length;

        setNotifications({
            [NOTIFICATION_TABS_MODEL.ALL.name]: { list: all, unviewed: unviewed },
            [NOTIFICATION_TABS_MODEL.REPORTS.name]: {
                list: reports,
                unviewed: reports.filter((e) => !e.read).length
            },
            [NOTIFICATION_TABS_MODEL.VISITORS.name]: {
                list: visitors,
                unviewed: visitors.filter((e) => !e.read).length
            }
        });
    }, [data, t]);

    const handleMarkOneAsRead = async (id, type) => {
    try {
        switch (type) {
            case NOTIFICATION_ITEM_MODEL.REPORTS.key:
                await dispatch(MarkFilesAsRead([id]));
                break;
            case NOTIFICATION_ITEM_MODEL.REQUEST_ACCESS.key:
                await dispatch(RequestAccessMarkFilesAsRead([id]));
                break;
            case NOTIFICATION_ITEM_MODEL.USER_INVITATION.key:
                await dispatch(InvitationMarkFilesAsRead([id]));
                break;
            default:
                return;
        }
        toast.success(t("has_been_marked_as_read"));
    } catch (error) {
        toast.error(t('something_went_wrong_2'));
    }
};
    return {
        dispatch,
        navigate,
        // states
        open,
        selected,
        setSelected,
        notifications,

        // setters & handlers
        handleToggle,
        handleClose,
        handleMarkAllReportsAsRead,
        showButton,
        handleMarkOneAsRead
    };
};
